import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Booking, Person } from '@gigdriver/domain';
import {
  getOrgPeople,
  createPerson as createPersonApi,
  updatePerson as updatePersonApi,
  deletePerson as deletePersonApi,
  getPersonBookings,
} from '@gigdriver/client';

export const usePeopleStore = defineStore('people', () => {
  const people = ref<Person[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  // One-shot, cached per personId — booking history is cold data (not worth
  // a live listener the way the gig/offers views need to be).
  const personHistory = ref<Map<string, Booking[]>>(new Map());
  const historyLoading = ref(false);

  async function loadPersonHistory(orgId: string, personId: string) {
    try {
      historyLoading.value = true;
      error.value = null;
      personHistory.value.set(personId, await getPersonBookings(orgId, personId));
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load booking history';
    } finally {
      historyLoading.value = false;
    }
  }

  async function loadOrgPeople(orgId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      people.value = await getOrgPeople(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load people';
    } finally {
      isLoading.value = false;
    }
  }

  async function createPerson(
    orgId: string,
    data: Omit<Person, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>,
  ) {
    try {
      error.value = null;
      await createPersonApi(orgId, data);
      await loadOrgPeople(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add person';
      throw err;
    }
  }

  async function updatePerson(orgId: string, personId: string, updates: Partial<Person>) {
    try {
      error.value = null;
      await updatePersonApi(orgId, personId, updates);
      await loadOrgPeople(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update person';
      throw err;
    }
  }

  async function deletePerson(orgId: string, personId: string) {
    try {
      error.value = null;
      await deletePersonApi(orgId, personId);
      people.value = people.value.filter(p => p.id !== personId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete person';
      throw err;
    }
  }

  return {
    people,
    isLoading,
    error,
    loadOrgPeople,
    createPerson,
    updatePerson,
    deletePerson,
    personHistory,
    historyLoading,
    loadPersonHistory,
  };
});
